import React, { memo, useEffect, useState } from "react";
import { Carousel } from "@mantine/carousel";
import "@mantine/carousel/styles.css";
import RecipeSuggestions from "./RecipeSuggestions";
import SelectedRecipeCard from "./SelectedRecipeCard";
import liveChatStyles from "../chat/live-chat.module.scss";
import { useCart, TCartRecipe, TProduct } from "../cart/cart-provider";
import "./RecipePanel.scss"; // We'll create this file for custom panel styles
import CuisineChooser from "./CuisineChooser";
import { Recipe } from "types/recipe";
import { performProductSearch } from "utils/search-utils";

import LeadGenModal from "../cart/LeadGenModal";

// Recipe Panel component
const RecipePanel = ({
  onSearch,
  onSearchRecipe,
}: {
  onSearch: (
    query: string,
    options: { serviceId: string }
  ) => Promise<TProduct[]> | TProduct[];
  onSearchRecipe: (
    query: string,
    options: { serviceId: string }
  ) => Promise<Recipe[]> | Recipe[];
}) => {
  // Get recipes from the cart context
  const {
    recipeList,
    removeRecipeItemsFromCart,
    addToCart,
    suggestedRecipes,
    addRecipe,
    removeRecipe,
    setRecipeList,
    ...cart
  } = useCart();

  // Add loading state
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadGenModal, setShowLeadGenModal] = useState(false);

  // Track recipes that are currently loading their prices
  const [loadingPriceRecipes, setLoadingPriceRecipes] = useState<string[]>([]);

  // Track the most recent action to prevent side effects
  const [lastAction, setLastAction] = useState<"add" | "remove" | "view" | "">(
    ""
  );

  // Force cart to be visible at all times
  useEffect(() => {
    // Always keep the cart view active
    cart.setStep("cart");
  }, [cart]);

  // Add side-effect prevention to cart.step changes
  useEffect(() => {
    // If an action other than 'view' caused step change, don't allow it to affect collapsing
    if (lastAction && lastAction !== "view") {
      setLastAction("");
    }
  }, [cart.step, lastAction]);

  // Generate slide indices for carousel based on suggestedRecipes length
  // If no suggestedRecipes available, show 3 slides with mock data
  const suggestionSlides = suggestedRecipes?.length
    ? Array.from({ length: Math.max(suggestedRecipes.length, 5) }, (_, i) => i)
    : Array.from({ length: 3 }, (_, i) => i);

  // Convert Recipe to format expected by SelectedRecipeCard
  const convertToSelectedRecipeFormat = (recipe: Recipe) => {
    // Static -10% discount for all recipes
    const discount = "-10%";

    // Convert tags from available data or generate mock tags
    const tags = [];

    // Add preparation time if available
    if (recipe.preparationTime) {
      tags.push(`${recipe.preparationTime} min`);
    }

    // Add a category if available
    if (recipe.categories && recipe.categories.length > 0) {
      tags.push(recipe.categories[0].name);
    } else {
      // Add a tag related to diet or cooking style if no categories
      const dietTags = [
        "Spicy",
        "Vegan",
        "Vegetarisch",
        "Gluten-Free",
        "Low-Carb",
      ];
      tags.push(dietTags[Math.floor(Math.random() * dietTags.length)]);
    }

    // Check if this recipe is currently loading its price
    const isLoadingPrice = loadingPriceRecipes.includes(recipe.recipeId);

    // Find the recipe in recipeList to get its calculated price
    const recipeWithPrice = recipeList.find((r) => r.id === recipe.recipeId);

    // Only set price if recipe has a price > 0 and is not loading
    const price =
      !isLoadingPrice && recipeWithPrice?.price && recipeWithPrice.price > 0
        ? `€${recipeWithPrice.price.toFixed(2)}`
        : ""; // Empty string triggers loading animation

    // Store difficulty separately to handle spoon icons in the component
    const difficulty = recipe.difficulty || "medium";

    return {
      id: recipe.recipeId,
      name: recipe.name,
      imageUrl: recipe.recipeImage?.large || recipe.recipeImage?.small || "",
      price: price,
      kcal: "",
      grams: "",
      discount: discount, // Always show -10% discount
      tags: tags,
      quantity: recipe.servingSize || 1,
      difficulty, // Pass difficulty separately to handle spoon icons
    };
  };

  // Handlers for recipe card interactions
  const handleIncreaseQuantity = (id: string) => {
    console.log("Increase quantity for:", id);

    cart.increaseRecipeQuantity(id);

    // Implement actual increase quantity logic
  };

  const handleDecreaseQuantity = (id: string) => {
    console.log("Decrease quantity for:", id);
    // If quantity becomes 0, we should remove the recipe
    // but without changing the view/step
    removeRecipeItemsFromCart(id);

    // Ensure we stay in recipe view by explicitly setting the step
    // This prevents any accidental view changes from the remove operation
    if (cart.step === "recipe") {
      setTimeout(() => {
        cart.setStep("recipe");
      }, 0);
    }
  };

  // Add to cart handler
  const handleAddToCart = async (id: string) => {
    console.log("Adding recipe to cart:", id);
    // Mark this as an add action to prevent unwanted view changes
    setLastAction("add");
    setIsLoading(true);

    // Find the recipe in suggestedRecipes
    const recipeToAdd = suggestedRecipes?.find(
      (recipe) => recipe.recipeId === id
    );

    if (recipeToAdd) {
      // Check if recipe already exists in cart to prevent duplicates
      const recipeExists = recipeList.some(
        (recipe) => recipe.id === recipeToAdd.recipeId
      );

      if (recipeExists) {
        console.log("Recipe already in cart:", recipeToAdd.name);
        setIsLoading(false);
        return;
      }

      // Create recipe cart item immediately
      const cartRecipe: TCartRecipe = {
        id: recipeToAdd.recipeId,
        name: recipeToAdd.name,
        description:
          recipeToAdd.instructionsPlainText || recipeToAdd.mainCategory || "",
        price: 0, // Start with 0 price until ingredients are loaded
        image: recipeToAdd.recipeImage?.large || "",
        ingredients: [], // Will be populated after search
        apiData: recipeToAdd, // Store the original API data
        quantity: recipeToAdd.servingSize || 1,
      };

      // Add recipe to cart immediately
      addRecipe(cartRecipe);

      // Mark recipe as loading price
      setPriceLoading(cartRecipe.id, true);

      try {
        // Then search for ingredients
        const ingredients = await performProductSearch({
          serviceId: cart.serviceId,
          items: recipeToAdd.ingredients
            .filter((ingredient) => Boolean(ingredient.description)) // Only include ingredients with descriptions
            .reduce((acc, curr) => {
              // Log ingredient details for debugging
              console.log(`Processing ingredient: ${curr.description}`, {
                quantity: curr.quantity,
                unit: curr.unit,
                fullData: curr,
              });

              // Use a proper key for merging - combine description and unit if present
              const ingredientKey = curr.unit
                ? `${curr.description.toLowerCase().trim()}_${curr.unit
                    .toLowerCase()
                    .trim()}`
                : curr.description.toLowerCase().trim();

              // Find existing ingredient by key for more accurate deduplication
              const existing = acc.find((item) => {
                const itemKey = item.unit
                  ? `${item.description.toLowerCase().trim()}_${item.unit
                      .toLowerCase()
                      .trim()}`
                  : item.description.toLowerCase().trim();
                return itemKey === ingredientKey;
              });

              // Use fallback quantity of 1 if quantity is missing
              const safeQuantity = curr.quantity || 1;

              if (existing?.quantity) {
                existing.quantity += safeQuantity;
                return acc;
              } else {
                return [
                  ...acc,
                  {
                    ...curr,
                    quantity: safeQuantity,
                    // Ensure unit is never undefined
                    unit: curr.unit || "",
                  },
                ];
              }
            }, [] as typeof recipeToAdd.ingredients)
            .map((ingredient) => ({
              name: ingredient.description.trim(), // Send only the ingredient description, not quantity/unit
            })),
          context: "",
          searchFunction: onSearch,
        });

        // Calculate the actual price based on ingredients
        const totalPrice = ingredients.reduce((acc, item) => {
          if (
            !item.product ||
            !item.product.price ||
            !item.product.price.value
          ) {
            return acc;
          }
          const price = Number(item.product.price.value);
          return acc + price * (item.quantity || 1);
        }, 0);

        // Round to 2 decimal places for proper money handling
        const roundedPrice = Math.round(totalPrice * 100) / 100;

        // Update recipe with ingredients and actual price
        setRecipeList((prev: TCartRecipe[]) =>
          prev.map((recipe: TCartRecipe) =>
            recipe.id === cartRecipe.id
              ? { ...recipe, ingredients, price: roundedPrice }
              : recipe
          )
        );

        // Recipe price is now loaded
        setPriceLoading(cartRecipe.id, false);

        // REMOVED: Don't automatically switch to cart view
      } catch (error) {
        console.error("Error searching for ingredients:", error);

        // Enhanced error reporting
        console.error("Recipe that failed:", {
          id: recipeToAdd.recipeId,
          name: recipeToAdd.name,
          totalIngredients: recipeToAdd.ingredients.length,
          ingredients: recipeToAdd.ingredients.map((ing) => ({
            description: ing.description,
            quantity: ing.quantity || "missing",
            unit: ing.unit || "missing",
          })),
        });

        // Recipe is no longer loading price (even though it failed)
        setPriceLoading(cartRecipe.id, false);

        // If there's an error, remove the recipe from the cart
        removeRecipe(cartRecipe.id);
      } finally {
        setIsLoading(false);
      }
    } else {
      console.warn("Recipe not found:", id);
      setIsLoading(false);
    }
  };



  // Handle clearing all recipes
  const handleClearAllRecipes = () => {
    // Mark this as a remove action to prevent unwanted view changes
    setLastAction("remove");

    // Remove all recipes without changing the view
    setRecipeList([]);

    // Don't automatically collapse or navigate - stay in recipe view
    // This ensures the user stays in the same view after clearing recipes

    // Force recipe view to ensure consistent UI state
    if (cart.step === "recipe") {
      setTimeout(() => {
        cart.setStep("recipe");
      }, 0);
    }
  };

  // Function to mark a recipe as loading or done loading price
  const setPriceLoading = (recipeId: string, isLoading: boolean) => {
    setLoadingPriceRecipes((prev) => {
      if (isLoading && !prev.includes(recipeId)) {
        return [...prev, recipeId];
      } else if (!isLoading) {
        return prev.filter((id) => id !== recipeId);
      }
      return prev;
    });
  };

  return (
    <div className="recipe-panel__section">
      <div className="recipe-panel__header button-style">
        <span>Recipes</span>
      </div>

      <div className="recipe-panel__content">
        {/* CuisineChooser in its own container */}
        <div className="recipe-panel__cuisine-chooser">
          <CuisineChooser />
        </div>

        {/* Recipe content in separate container */}
        <div className="recipe-panel__recipe-content">
          {/* Recipe Suggestions Carousel */}
          {suggestedRecipes?.length > 0 && (
            <Carousel
              slideSize="auto"
              align="start"
              slidesToScroll={2}
              containScroll="keepSnaps"
              withControls={true}
              draggable
              style={{
                width: "100%",
                maxWidth: 600,
                margin: "10px auto",
              }}
              slideGap="md"
            >
              {suggestionSlides.map((index) => (
                <Carousel.Slide
                  key={index}
                  style={{ flexBasis: "auto", paddingBottom: 12 }}
                >
                  <RecipeSuggestions
                    recipeIndex={index}
                    recipe={
                      suggestedRecipes[index % suggestedRecipes.length]
                    }
                    onAddToCart={handleAddToCart}
                  />
                </Carousel.Slide>
              ))}
            </Carousel>
          )}
          <div className="recipe-panel__list-container">
            {recipeList.length > 0 && (
              <div className="recipe-panel__list">
                {recipeList.map((recipe) => (
                  <SelectedRecipeCard
                    key={recipe.id}
                    recipe={
                      recipe.apiData
                        ? convertToSelectedRecipeFormat(recipe.apiData)
                        : {
                            id: recipe.id,
                            name: recipe.name,
                            imageUrl: recipe.image,
                            price:
                              recipe.price > 0
                                ? `€${recipe.price.toFixed(2)}`
                                : "",
                            kcal: "",
                            grams: "",
                            discount: "-10%", // Static -10% discount
                            tags: ["Quick", "Easy"],
                            quantity: recipe.quantity || 1,
                            difficulty: "medium", // Default medium difficulty
                          }
                    }
                    onIncreaseQuantity={handleIncreaseQuantity}
                    onDecreaseQuantity={handleDecreaseQuantity}
                  />
                ))}
                <button
                  className="recipe-panel__clear-all"
                  onClick={handleClearAllRecipes}
                >
                  Remove all recipes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bestellen button outside of scrollable content */}
      <button
        className={liveChatStyles.zumWarenkorbButton}
        onClick={() => setShowLeadGenModal(true)}
        disabled={isLoading}
        style={{ height: "50px", minHeight: "50px" }}
      >
        {isLoading ? (
          <div
            className="neo-icon no-shadow"
            style={{
              height: "24px",
              width: "24px",
              display: "inline-flex",
              padding: 0,
              margin: 0,
            }}
          >
            <button
              className="icon__inn no-shadow"
              value="submit"
              style={{
                height: "24px",
                width: "24px",
                padding: 0,
                margin: 0,
              }}
            >
              <svg
                fill="none"
                stroke="var(--primary-color)"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                width="24"
                height="24"
                className="recipe-panel__loading-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                ></path>
              </svg>
            </button>
          </div>
        ) : (
          <span style={{ height: "24px", lineHeight: "24px" }}>Order</span>
        )}
      </button>

      <LeadGenModal 
        isOpen={showLeadGenModal} 
        onClose={() => setShowLeadGenModal(false)} 
      />
    </div>
  );
};

export default memo(RecipePanel);
